import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, ExternalLink, Eye, Users, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import CardPreview from "@/components/xuma/CardPreview";
import DashLayout from "@/components/xuma/DashLayout";
import SubscriptionCard from "@/components/xuma/SubscriptionCard";
import DangerZone from "@/components/xuma/DangerZone";

export default function Dashboard() {
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/card/me").then(({ data }) => setCard(data)).catch(() => {});
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const publicUrl = card ? `${window.location.origin}/c/${card.slug}` : "";

  return (
    <DashLayout>
      <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-xuma-dark tracking-tight" data-testid="dashboard-welcome">
        Tu tarjeta digital
      </h1>
      <p className="mt-2 text-xuma-muted">Personaliza, publica y comparte. Cada contacto suma.</p>

      <div className="mt-8 grid lg:grid-cols-2 gap-8 items-start">
        <div className="rounded-3xl bg-white border border-xuma-border p-8">
          {card ? <CardPreview card={card} publicUrl={publicUrl} />
            : <div className="aspect-[1.58/1] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-xuma-primary" /></div>}
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            {card?.published
              ? <span className="inline-flex items-center gap-1.5 text-sm text-green-700 bg-green-50 rounded-full px-3 py-1"><CheckCircle2 className="h-4 w-4" /> Publicada</span>
              : <span className="inline-flex items-center gap-1.5 text-sm text-xuma-muted bg-xuma-dash rounded-full px-3 py-1">Borrador</span>}
            <span className="inline-flex items-center gap-1.5 text-sm text-xuma-muted"><Eye className="h-4 w-4" /> {stats?.views ?? 0} vistas</span>
            <span className="inline-flex items-center gap-1.5 text-sm text-xuma-muted"><Users className="h-4 w-4" /> {stats?.total_leads ?? 0} prospectos</span>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button data-testid="edit-card-btn" onClick={() => navigate("/editor")} className="bg-xuma-primary hover:bg-xuma-primaryHover text-white rounded-full gap-2">
              <Pencil className="h-4 w-4" /> Editar tarjeta
            </Button>
            {card?.published && (
              <a href={publicUrl} target="_blank" rel="noreferrer">
                <Button variant="outline" data-testid="view-public-btn" className="rounded-full gap-2 border-xuma-border">
                  <ExternalLink className="h-4 w-4" /> Ver tarjeta pública
                </Button>
              </a>
            )}
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4">
            <button onClick={() => navigate("/crm")} className="text-left rounded-2xl bg-white border border-xuma-border p-5 hover:-translate-y-1 transition-transform">
              <Users className="h-5 w-5 text-xuma-primary" />
              <h3 className="mt-3 font-heading font-bold text-xuma-dark">Mini CRM</h3>
              <p className="text-sm text-xuma-muted mt-1">Da seguimiento a tus prospectos.</p>
            </button>
            <button onClick={() => navigate("/stats")} className="text-left rounded-2xl bg-white border border-xuma-border p-5 hover:-translate-y-1 transition-transform">
              <Eye className="h-5 w-5 text-xuma-primary" />
              <h3 className="mt-3 font-heading font-bold text-xuma-dark">Estadísticas</h3>
              <p className="text-sm text-xuma-muted mt-1">Mide visitas y resultados.</p>
            </button>
          </div>

          <SubscriptionCard />
          <DangerZone />
        </div>
      </div>
    </DashLayout>
  );
}
